import React, { useEffect, useMemo } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Home from './components/Home';
import About from './components/About';
import Products from './components/Products';
import ProductDetail from './components/ProductDetail';
import Catalogues from './components/Catalogues';
import Contact from './components/Contact';
import WhatsAppButton from './components/WhatsAppButton';
import Footer from './components/Footer';

const seoMap = {
  '/': {
    title: 'Zyvora Life Sciences 9 | Premium Livestock Nutrition & Cattle Feed Supplements',
    description: 'Premium agricultural science brand for cattle and buffalo nutrition, dairy productivity, dealer support, and distributor-ready livestock feed supplements.'
  },
  '/about': {
    title: 'About Zyvora Life Sciences 9 | Advanced Animal Nutrition',
    description: 'Learn about Zyvora Life Sciences 9, a modern livestock nutrition company focused on premium cattle health, scientific formulations, and farmer-first support.'
  },
  '/products': {
    title: 'Products | Zyvora Life Sciences 9 Livestock Nutrition Portfolio',
    description: 'Explore Zyvora Life Sciences 9 cattle nutrition products with benefits, ingredients, usage instructions, packaging sizes, and inquiry CTAs.'
  },
  '/catalogues': {
    title: 'Catalogue Download | Zyvora Life Sciences 9 Product Literature',
    description: 'Download premium Zyvora Life Sciences 9 livestock nutrition catalogues after submitting a short lead form for follow-up support.'
  },
  '/contact': {
    title: 'Contact & Distributor Inquiry | Zyvora Life Sciences 9',
    description: 'Submit general, distributor, and catalogue enquiries for Zyvora Life Sciences 9 livestock nutrition products and bulk supply support.'
  }
};

function setMeta(selector, attribute, value) {
  const element = document.querySelector(selector);
  if (element) element.setAttribute(attribute, value);
}

function MainPage() {
  const location = useLocation();

  const sectionRefs = useMemo(
    () => ({
      '/': React.createRef(),
      '/about': React.createRef(),
      '/products': React.createRef(),
      '/catalogues': React.createRef(),
      '/contact': React.createRef()
    }),
    []
  );

  useEffect(() => {
    const matched = seoMap[location.pathname] || seoMap['/'];
    document.title = matched.title;
    setMeta('meta[name="description"]', 'content', matched.description);
    setMeta('meta[property="og:title"]', 'content', matched.title);
    setMeta('meta[property="og:description"]', 'content', matched.description);
    setMeta('meta[property="og:url"]', 'content', window.location.href);
  }, [location.pathname]);

  useEffect(() => {
    const targetRef = sectionRefs[location.pathname] || sectionRefs['/'];
    if (targetRef?.current) targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location.pathname, sectionRefs]);

  return (
    <>
      <Navbar />
      <main>
        <section id="home" ref={sectionRefs['/']}><Home /></section>
        <section id="about" ref={sectionRefs['/about']}><About /></section>
        <section id="products" ref={sectionRefs['/products']}><Products /></section>
        <section id="catalogues" ref={sectionRefs['/catalogues']}><Catalogues /></section>
        <section id="contact" ref={sectionRefs['/contact']}><Contact /></section>
      </main>
      <Footer />
      <WhatsAppButton />
    </>
  );
}

function ProductPageShell() {
  return (
    <>
      <Navbar />
      <ProductDetail />
      <Footer />
      <WhatsAppButton />
    </>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/" element={<MainPage />} />
      <Route path="/about" element={<MainPage />} />
      <Route path="/products" element={<MainPage />} />
      <Route path="/products/:slug" element={<ProductPageShell />} />
      <Route path="/catalogues" element={<MainPage />} />
      <Route path="/contact" element={<MainPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
